<template>
  <div class="cm-lang-selector">
    <button
      class="cm-lang-toggle"
      ref="toggleButton"
      :aria-expanded="isOpen"
      aria-label="Select language"
    >
      <span class="cm-lang-flag">{{ getCurrentFlag() }}</span>
      <span class="cm-lang-code">{{ locale.toUpperCase() }}</span>
      <svg
        class="cm-lang-arrow"
        :class="{ 'cm-lang-arrow-open': isOpen }"
        viewBox="0 0 24 24"
        width="16"
        height="16"
      >
        <path d="M7 10l5 5 5-5z" />
      </svg>
    </button>

    <div v-if="isOpen" class="cm-lang-dropdown" ref="dropdown">
      <button
        v-for="lang in availableLanguages"
        :key="lang.code"
        class="cm-lang-option"
        :class="{ 'cm-lang-option-active': lang.code === locale }"
        @click.stop="selectLanguage(lang.code)"
      >
        <span class="cm-lang-flag">{{ lang.flag }}</span>
        <span class="cm-lang-name">{{ lang.name }}</span>
        <span class="cm-lang-code">{{ lang.code.toUpperCase() }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { SupportedLocale } from '../types'

  const { locale } = useI18n()

  interface Language {
    code: SupportedLocale
    name: string
    flag: string
  }

  const availableLanguages: Language[] = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
    { code: 'no', name: 'Norsk', flag: '🇳🇴' },
    { code: 'da', name: 'Dansk', flag: '🇩🇰' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  ]

  const isOpen = ref(false)
  const dropdown = ref<HTMLElement>()
  const toggleButton = ref<HTMLElement>()

  function getCurrentFlag(): string {
    const currentLang = availableLanguages.find(lang => lang.code === locale.value)
    return currentLang?.flag || '🇺🇸'
  }

  function toggleDropdown() {
    isOpen.value = !isOpen.value
  }

  function selectLanguage(code: SupportedLocale) {
    locale.value = code
    isOpen.value = false

    // Dispatch custom event for external listeners (like dev tools)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('contactme:change-locale', {
          detail: { locale: code },
        })
      )
    }
  }

  function handleClickOutside(event: Event) {
    const target = event.target as HTMLElement
    if (
      isOpen.value &&
      dropdown.value &&
      !dropdown.value.contains(target) &&
      !target.closest('.cm-lang-toggle')
    ) {
      isOpen.value = false
    }
  }

  onMounted(() => {
    // Add direct click listener to the button to ensure it works
    if (toggleButton.value) {
      toggleButton.value.addEventListener('click', e => {
        e.stopPropagation()
        toggleDropdown()
      })
    }

    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
</script>
